import { AccountMapper } from '@core/mapper/accountMapper';
import { AccountAnalysisStateMapper } from '@core/mapper/accountAnalysisStateMapper';
import { Bad } from '@core/model/framework/Result/BadResult';
import { Ok } from '@core/model/framework/Result/OkResult';
import { Result } from '@core/model/framework/Result/Result';
import type { HttpClient } from '@core/model/framework/httpClient';
import {
  AccountService,
  GetAccountsInput,
  GetAccountsOutput,
  GetCaseStateServiceInput,
  GetCaseStateServiceOutput,
  RequestAnalysisServiceInput,
  RequestAnalysisServiceOutput,
  SynchronizeAccountsServiceInput,
  SynchronizeAccountsServiceOutput,
} from '@core/model/service/accountService';
import { SERVICE_ID } from '@di/serviceId';
import { inject, injectable } from 'inversify';
import { GetAccountsResponse } from './types/request-response/getAccounts';
import { GetCaseStateResponse } from './types/request-response/getCaseState';
import {
  PostRequestAnalysisRequest,
  PostRequestAnalysisResponse,
} from './types/request-response/postRequestAnalysis';
import {
  PutSynchronizeAccountsRequest,
  PutSynchronizeAccountsResponse,
} from './types/request-response/putSynchronizeAccounts';

@injectable()
export class DFAccountService implements AccountService {
  constructor(
    @inject(SERVICE_ID.openBankingHttpClient)
    private readonly httpClient: HttpClient,
    @inject(AccountAnalysisStateMapper)
    private readonly accountAnalysisStateMapper: AccountAnalysisStateMapper,
    @inject(AccountMapper)
    private readonly accountMapper: AccountMapper
  ) {}

  async requestAnalysis(input: RequestAnalysisServiceInput): RequestAnalysisServiceOutput {
    const { userToken, userId, bankAccountsIds } = input;

    const httpResult = await this.httpClient.post<PostRequestAnalysisResponse, PostRequestAnalysisRequest>(
      `/users/${userId}/open-banking-case-clerk/request-analysis`,
      { bankAccountsIds },
      {
        Authorization: `Bearer ${userToken}`,
      }
    );

    if (httpResult.success === false && 'error' in httpResult.value.data) {
      if (httpResult.value.data.error.reason === 'SOME_BANK_ACCOUNTS_ARE_NOT_SYNCHRONIZED_YET') {
        return Bad('SOME_BANK_ACCOUNTS_ARE_NOT_SYNCHRONIZED_YET');
      } else {
        return Bad('CASE_IN_INCORRECT_STATUS');
      }
    }

    return Ok();
  }

  async synchronizeAccounts(input: SynchronizeAccountsServiceInput): SynchronizeAccountsServiceOutput {
    const { userId, userToken, selectedAccountsId, connectionId } = input;

    const synchronizeAccountsRequestBody: PutSynchronizeAccountsRequest = {
      accounts: selectedAccountsId.map((accountId) => {
        return {
          id: accountId,
          isSynchronizationEnabled: true,
        };
      }),
    };

    const httpResult = await this.httpClient.put<
      PutSynchronizeAccountsResponse,
      PutSynchronizeAccountsRequest
    >(`/users/${userId}/connections/${connectionId}/accounts`, synchronizeAccountsRequestBody, {
      Authorization: `Bearer ${userToken}`,
    });

    Result.unwrap(httpResult);

    return Ok();
  }

  async getCaseState(input: GetCaseStateServiceInput): GetCaseStateServiceOutput {
    const { userId, userToken } = input;

    const httpResult = await this.httpClient.get<GetCaseStateResponse>(
      `/users/${userId}/open-banking-case-clerk`,
      {
        Authorization: `Bearer ${userToken}`,
      }
    );

    const caseClerk = Result.unwrap(httpResult).data;

    if ('error' in caseClerk) {
      return Bad('CASE_IN_INCORRECT_STATUS');
    }

    return Ok(this.accountAnalysisStateMapper.toDomain(caseClerk));
  }

  async getAccounts(input: GetAccountsInput): GetAccountsOutput {
    const { userId, userToken, connectionId } = input;

    const httpResult = await this.httpClient.get<GetAccountsResponse>(
      `/users/${userId}/connections/${connectionId}/accounts`,
      {
        Authorization: `Bearer ${userToken}`,
      }
    );

    const accountsDto = Result.unwrap(httpResult);
    const accounts = accountsDto.data.map((accountDto) => this.accountMapper.toDomain(accountDto));

    return Ok(accounts);
  }
}
